import { io, Socket } from 'socket.io-client';
import { atom, useAtomValue, injectMemo, injectAtomState, injectEffect, injectAtomInstance, injectAtomValue } from '@zedux/react';
import { IUserInfo, IGroupUsers } from '../types';

// @ts-ignore
const URL = import.meta.env.VITE_WS_URL

function getCookie(name: string): string | null {
    if (typeof document === 'undefined') {
        return null
    }

    const cookies = document.cookie.split(';').map((cookie) => cookie.trim())
    const match = cookies.find((cookie) => cookie.startsWith(`${name}=`))

    if (!match) {
        return null
    }

    return decodeURIComponent(match.slice(name.length + 1))
}

export function getSocketConfig(): string | null {
    const globalJwt = (globalThis as typeof globalThis & {
        dusco_config?: Record<string, unknown>,
    })?.dusco_config?.chat_jwt;

    if (typeof globalJwt === 'string' && globalJwt) {
        return globalJwt;
    }

    return getCookie('temporary_jwt') ?? null;
}

export const jwtAtom = atom<string | null>('jwt', getSocketConfig());

function initializeSocket(token: string | null): Socket | null {

    if (!token) {
        return null;
    }

    return io(URL, {
        auth: {
            token,
        },
        withCredentials: true,
        transports: ["websocket"],
        autoConnect: false, // Connection will be started lazily
        timeout: 10000, // 10 seconds in milliseconds
        reconnection: false,
    });
}

// 1. Define an atom to hold the connection state (the "active" socket)
export const activeSocketAtom = atom<Socket | null>('activeSocket', null);

export const userInfoAtom = atom<IUserInfo | null>('userInfo', null);

export const contactsListAtom = atom<IGroupUsers>('contactsList', {});

export const socketAtom = atom<Socket | null>('socket', () => {
    const token = injectAtomValue(jwtAtom)

    // initializeSocket will only be called once when the atom is first used
    const socket = injectMemo(() => initializeSocket(token), [token])

    // 2. Use injectAtomState to manage and listen to the connection state
    const [currentSocket, setCurrentSocket] = injectAtomState(activeSocketAtom)

    const jwtStore = injectAtomInstance(jwtAtom)
    const userInfoStore = injectAtomInstance(userInfoAtom)
    const contactsListStore = injectAtomInstance(contactsListAtom)

    injectEffect(() => {
        if (socket == null) {
            return
        }

        let reconnectInterval: number | null = null
        let connectAttemptInFlight = false
        let lastAttemptAt = 0
        let retryCount = 0
        let lastWakeCheck = Date.now()
        const connectAttemptTimeout = 15000

        const getRetryDelay = () => {
            if (retryCount < 3) return 3000
            if (retryCount < 6) return 5000
            if (retryCount < 10) return 7000
            return 10000
        }

        const clearReconnectLoop = () => {
            if (reconnectInterval !== null) {
                clearInterval(reconnectInterval)
                reconnectInterval = null
            }
        }

        const isConnectAttemptStale = () => {
            return connectAttemptInFlight && Date.now() - lastAttemptAt >= connectAttemptTimeout
        }

        const reconnectNowIfNeeded = () => {
            if (socket.connected) {
                console.log('Socket is already connected', socket.connected, socket.active, socket.disconnected)
                return
            }

            if (connectAttemptInFlight && !isConnectAttemptStale()) {
                console.log('Reconnect attempt already in flight', socket.connected, socket.active, socket.disconnected)
                return
            }

            if (isConnectAttemptStale() || socket.active || socket.disconnected === false) {
                console.log('Resetting stale socket before reconnect', socket.connected, socket.active, socket.disconnected)
                connectAttemptInFlight = false
                socket.disconnect()
            }

            lastAttemptAt = Date.now()
            retryCount++
            connectAttemptInFlight = true

            console.log(
                `Reconnect attempt #${retryCount} (${getRetryDelay()}ms)`
            );

            socket.connect()
        };

        const ensureReconnectLoop = () => {
            if (reconnectInterval !== null) return

            reconnectInterval = window.setInterval(() => {
                console.log('Reconnect loop tick')

                const now = Date.now()

                // detect browser hibernation / timer freeze
                const drift = now - lastWakeCheck

                if (drift > 15000) {
                    console.log('Wake from hibernation detected')
                    reconnectNowIfNeeded()
                }

                lastWakeCheck = now

                if (socket.connected) {
                    clearReconnectLoop()
                    return
                }

                const delay = getRetryDelay()

                if (now - lastAttemptAt >= delay) {
                    reconnectNowIfNeeded()
                }
            }, 1000)
        }

        const handleConnect = () => {
            console.log('Socket connected:', socket.id)
            connectAttemptInFlight = false
            retryCount = 0
            lastAttemptAt = 0
            setCurrentSocket(socket)
            clearReconnectLoop()
        };

        const handleDisconnect = () => {
            console.log('Socket disconnected')
            connectAttemptInFlight = false
            setCurrentSocket(null)
            ensureReconnectLoop()
        }

        const handleConnectError = (error: Error) => {
            console.log('Socket connect error', error.message)
            connectAttemptInFlight = false
            setCurrentSocket(null)

            const message = error?.message ?? ''
            const isInvalidTokenError = message.includes('Invalid token') || message.includes('Authentication error')

            if (isInvalidTokenError) {
                const currentJwt = jwtStore.getState() as string | null;
                const cookieJwt = getCookie('temporary_jwt');
                if (currentJwt && cookieJwt && currentJwt === cookieJwt) {
                    console.log('Clearing stale temporary JWT from cookie and atom state')

                    // Clear the temporary JWT from the cookie and atom state
                    if (typeof document !== 'undefined') {
                        document.cookie = 'temporary_jwt=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;'
                    }

                    // Clear the JWT from the global window object
                    const sharedWindow = globalThis as typeof globalThis & {
                        dusco_config?: Record<string, unknown>,
                    }
                    if (sharedWindow.dusco_config) {
                        delete sharedWindow.dusco_config.chat_jwt
                    }

                    // Clear the JWT from the atom state
                    jwtStore.setState(null)
                }
            }

            ensureReconnectLoop()
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('Tab resumed');

                if (!socket.connected) {
                    reconnectNowIfNeeded()
                    ensureReconnectLoop()
                }
            }
        }

        const handleWindowReconnectEvent = () => {
            console.log('Received socket-reconnect event from window')
            reconnectNowIfNeeded()
            ensureReconnectLoop()
        }

        const handleUserInfo = (userInfo: IUserInfo) => {
            if (userInfoStore.getState() == null) {
                console.log('Received user info from socket', userInfo)
                userInfoStore.setState(userInfo)
            }
        }

        const handleContactsList = (users: IGroupUsers) => {
            console.log('received contacts list', users)
            contactsListStore.setState(users)
        }

        socket.on('connect', handleConnect)
        socket.on('disconnect', handleDisconnect)
        socket.on('connect_error', handleConnectError)
        socket.on('user_info', handleUserInfo)
        socket.on('contacts_list', handleContactsList)

        // Debug
        // socket.on("connect_error", (e) => console.log("connect_error", e.message, new Date()));
        // socket.io.on("reconnect_attempt", (n) => console.log("reconnect_attempt", n, new Date()));
        // socket.io.on("reconnect", (n) => console.log("reconnect", n, new Date()));
        // socket.io.on("reconnect_failed", () => console.log("reconnect_failed", new Date()));
        // socket.io.on("error", (e) => console.log("manager error", e, new Date()));

        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('socket-reconnect', handleWindowReconnectEvent)

        if (socket.connected) {
            handleConnect()
        } else {
            reconnectNowIfNeeded()
            ensureReconnectLoop()
        }

        return () => {
            clearReconnectLoop()

            socket.off('connect', handleConnect)
            socket.off('disconnect', handleDisconnect)
            socket.off('connect_error', handleConnectError)
            socket.off('user_info', handleUserInfo)
            socket.off('contacts_list', handleContactsList)

            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('socket-reconnect', handleWindowReconnectEvent)
        }
    }, [socket])

    return currentSocket
})

export const useSocket = (): Socket | null => {
    return useAtomValue(socketAtom)
}


